import { Loader } from '@octopus-do/octopus-storybook'
import { parseNestError } from '@store/reducers/dashboard/utils'
import { ErrorNotification } from '@storeOld/reducers/notification'
import type React from 'react'
import { useEffect, useReducer, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams, useSearchParams } from 'react-router-dom'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useProjectPermissions } from '@/hooks'
import { NestClient } from '@/nest'
import {
  CrawlRunner,
  GuestHeader,
  Header,
  PagesSection,
  ProjectInfoCard,
  ProjectStats,
  PromoCards,
} from './components'
import {
  useMarkReportSeen,
  useRecrawlAccess,
  useReportOverview,
  useReportPages,
  useReportProjectMeta,
  useReportQuery,
  useSaveGuestReport,
} from './hooks'
import { RawReport } from './RawReport'
import css from './styles.module.css'
import type { CrawlResult } from './types'
import { formatComparison, formatCrawlTime, toSectionStats } from './utils'

// Long enough for a smooth scroll from the bottom of a full report to land.
const HOLD_MS = 600

export const SitemapReport: React.FC = () => {
  const {
    activeSection,
    changes,
    search,
    setSearch,
    setSection,
    setSort,
    sort,
  } = useReportQuery()
  const { uuid } = useParams<{ uuid: string }>()
  const [searchParams] = useSearchParams()
  const { error, loading, overview, reload } = useReportOverview(uuid)
  const recrawl = useRecrawlAccess()
  const dispatch = useDispatch()

  const meta = useReportProjectMeta(uuid)
  const guestSave = useSaveGuestReport(uuid, meta.ownerless, meta.reload)
  useMarkReportSeen(uuid, overview?.currentAt)

  // The permission matrix is normally built by the canvas, which a report never
  // mounts — without this the report's share dialog sees a user with no rights
  // at all, so the invite field and the per-member controls never render.
  useProjectPermissions()

  const [version, refetchPages] = useReducer((count: number) => count + 1, 0)
  const pages = useReportPages(uuid, {
    changes,
    search,
    section: activeSection,
    sort,
    version,
  })
  const crawling = Boolean(overview?.crawling)
  const wasCrawling = useRef(crawling)

  // A crawl the person started runs in this tab, and the run it belongs to is
  // what the progress panel is keyed by: a second re-crawl must start on a hook
  // that has never finished, not on the last run's finished state.
  const [runId, setRunId] = useState(0)
  const [saving, setSaving] = useState(false)
  const [holdHeight, setHoldHeight] = useState(0)
  const releaseHold = useRef(0)
  const recrawling = runId > 0

  useEffect(
    () => () => {
      window.clearTimeout(releaseHold.current)
    },
    []
  )

  // A crawl that lands replaces the whole report, so the list has to be asked
  // again — the counters come with the overview the poll already fetched.
  useEffect(() => {
    if (wasCrawling.current && !crawling) {
      refetchPages()
    }

    wasCrawling.current = crawling
  }, [crawling])

  // TEMPORARY (OCT-4636): see RawReport.
  if (searchParams.get('raw') === '1') {
    return <RawReport uuid={uuid} />
  }

  const scrollToTop = () => {
    window.scrollTo({ behavior: 'smooth', top: 0 })
  }

  // Whatever the reader had scrolled to is about to be replaced, so the screen
  // goes back to the top as the list gives way to the crawl. The page keeps the
  // height it had until the scroll lands: the list is what makes it long, and a
  // page that shrinks under a running scroll is clamped to the top at once —
  // a jump instead of the move.
  const handleRecrawl = () => {
    sendGtmEvent(GtmTriggerName.REPORT_RECRAWL_INIT)
    setHoldHeight(document.body.scrollHeight)
    scrollToTop()
    setRunId(Date.now())

    releaseHold.current = window.setTimeout(() => setHoldHeight(0), HOLD_MS)
  }

  const handleCancel = () => {
    setRunId(0)
  }

  const handleError = (message: string) => {
    dispatch(ErrorNotification(message))
    setRunId(0)
  }

  // The crawl only becomes the report once the API has taken it, so the panel
  // stays up — saying so — until the new counters and the new list are in.
  const handleFinished = (crawl: CrawlResult) => {
    if (!uuid || !overview) {
      return
    }

    setSaving(true)

    NestClient.sitemapReport
      .crawl(uuid, {
        faviconUrl: crawl.faviconUrl,
        pages: crawl.pages,
        title: crawl.title,
        url: crawl.url,
      })
      .then(() => {
        sendGtmEvent(GtmTriggerName.REPORT_RECRAWL_COMPLETED)
        reload()
        refetchPages()
      })
      .catch((cause) => dispatch(ErrorNotification(parseNestError(cause))))
      .finally(() => {
        setSaving(false)
        setRunId(0)
      })
  }

  // The counters and the list both hang off the crawl the report points at, so
  // there is nothing partial to show: either the report has one, or it says why
  // it does not.
  const message = () => {
    if (loading) {
      return null
    }

    if (error) {
      return error
    }

    if (overview?.status === 'crawling') {
      return 'The first crawl is still running. This page fills in when it finishes.'
    }

    if (overview?.status === 'failed') {
      return 'The crawl failed. Run it again to build the report.'
    }

    return null
  }

  const notice = message()

  return (
    <div className={css.stmp_report}>
      {meta.ownerless ? (
        <GuestHeader onSave={guestSave.save} saving={guestSave.saving} />
      ) : (
        <Header
          canEdit={recrawl.allowed}
          crawling={crawling || recrawling}
          onRecrawl={handleRecrawl}
          onScheduled={reload}
          schedule={overview?.schedule ?? null}
          updatedAt={formatCrawlTime(overview?.currentAt ?? null)}
        />
      )}

      <div
        className={css.stmp_report_content}
        style={{ minHeight: holdHeight }}
      >
        {loading && <Loader color="gray" threeDots />}

        {notice && <div className={css.stmp_report_notice}>{notice}</div>}

        {!notice && overview?.status === 'ready' && (
          <>
            <ProjectInfoCard
              url={overview.site.url}
              favicon={overview.site.faviconUrl}
              title={overview.site.title}
            />

            {recrawling ? (
              <>
                <CrawlRunner
                  key={runId}
                  pro={recrawl.pro}
                  reportId={uuid ?? ''}
                  saving={saving}
                  siteUrl={overview.site.url}
                  onCancel={handleCancel}
                  onError={handleError}
                  onFinished={handleFinished}
                />
                <PromoCards />
              </>
            ) : (
              <>
                <ProjectStats
                  stats={toSectionStats(overview.summary)}
                  activeSection={activeSection}
                  changes={changes}
                  comparison={formatComparison(
                    overview.currentAt,
                    overview.previousAt
                  )}
                  onSectionChange={setSection}
                />
                <PagesSection
                  activeSection={activeSection}
                  changes={changes}
                  hasMore={pages.hasMore}
                  items={pages.items}
                  loading={pages.loading}
                  loadingMore={pages.loadingMore}
                  onLoadMore={pages.loadMore}
                  onSearchChange={setSearch}
                  onSortChange={setSort}
                  search={search}
                  sort={sort}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
